import React, { useState } from "react";
import PropTypes from "prop-types";

import { sanitizeConsentHtml } from "../buildSignupFormEmbed";
import { FIELD_ORDER, FIELD_INPUT_TYPES, CHANNEL_OPTIONS, channelsWithAgreement, isRenderableQuestion } from "../signupFormFields";

const CheckCircleIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
    <path d="m9 11 3 3L22 4" />
  </svg>
);

const QUESTION_INPUT_TYPES = { number: "number", date: "date" };

const QuestionPreview = ({ question }) => {
  const label = `${question.label}${question.required ? " *" : ""}`;
  const options = Array.isArray(question.options) ? question.options : [];

  if (question.type === "checkboxes") {
    return (
      <fieldset className="sf-form-group">
        <legend className="sf-form-label">{label}</legend>
        {options.map((option) => (
          <label key={option} className="sf-form-choice"><input type="checkbox" readOnly />{option}</label>
        ))}
      </fieldset>
    );
  }

  return (
    <>
      <label className="sf-form-label">{label}</label>
      {question.type === "long_text" && <textarea className="sf-form-input sf-form-textarea" rows={4} placeholder={question.placeholder || ""} readOnly />}
      {question.type === "select" && (
        <select className="sf-form-input" defaultValue="">
          <option value="">Choose…</option>
          {options.map((option) => <option key={option} value={option}>{option}</option>)}
        </select>
      )}
      {!["long_text", "select"].includes(question.type) && (
        <input className="sf-form-input" type={QUESTION_INPUT_TYPES[question.type] || "text"} placeholder={question.placeholder || ""} readOnly />
      )}
    </>
  );
};

QuestionPreview.propTypes = {
  question: PropTypes.shape({
    type: PropTypes.string,
    label: PropTypes.string,
    placeholder: PropTypes.string,
    required: PropTypes.bool,
    options: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
};

const FormPreview = ({ fields, channels, letChoose, consents, questions = [], appearance, text, success }) => {
  const vars = {
    "--ht-bg": appearance.background_color || "#ffffff",
    "--ht-btn-bg": appearance.button_color || "#1982C4",
    "--ht-btn-text": appearance.button_text_color || "#ffffff",
    "--ht-text": appearance.text_color || "#141514",
    "--ht-border": appearance.input_border_color || "#cccccc",
    "--ht-radius": `${appearance.corner_radius ?? 0}px`,
  };
  const [done, setDone] = useState(false);
  const visibleFields = FIELD_ORDER
    .filter((k) => (k === "email" ? true : fields[k]?.enabled !== false))
    .map((k) => ({ k, ...(fields[k] || {}) }));
  const agreed = channelsWithAgreement(consents);
  const channelOpts = letChoose
    ? CHANNEL_OPTIONS.filter(({ key }) => channels[key] && !agreed.has(key))
    : [];

  return (
    <div className={`sf-form-card${appearance.drop_shadow ? " sf-shadow" : ""}`} style={vars}>
      {!done ? (
        <div>
          {text.heading && <h3 className="sf-form-heading">{text.heading}</h3>}
          {text.subheading && <p className="sf-form-sub">{text.subheading}</p>}
          {visibleFields.map(({ k, label, required }) => (
            <React.Fragment key={k}>
              <label className="sf-form-label">{label}{(k === "email" || required) ? " *" : ""}</label>
              <input className="sf-form-input" type={FIELD_INPUT_TYPES[k] || "text"} placeholder={(fields[k] || {}).placeholder || ""} readOnly />
            </React.Fragment>
          ))}
          {questions.filter(isRenderableQuestion).map((q) => <QuestionPreview key={q.key} question={q} />)}
          {channelOpts.map(({ key, label, checked }) => (
            <label key={key} className="sf-form-consent"><input type="checkbox" checked={checked} readOnly />{label}</label>
          ))}
          {consents.filter((c) => (c.text || "").trim()).map((c) => (
            <label key={c.cid} className="sf-form-consent">
              <input type="checkbox" checked={!!c.checked} readOnly />
              <span dangerouslySetInnerHTML={{ __html: `${sanitizeConsentHtml(c.text)}${c.required ? ' <span style="opacity:.7">*</span>' : ""}` }} />
            </label>
          ))}
          <button className="sf-form-btn" onClick={() => { setDone(true); setTimeout(() => setDone(false), 1800); }}>
            {text.button_label || "Subscribe"}
          </button>
        </div>
      ) : (
        <div className="sf-form-success" style={{ display: "flex" }}>
          <CheckCircleIcon />
          <span style={{ fontWeight: 600 }}>{success.message || "Thanks for subscribing!"}</span>
        </div>
      )}
      <div className="sf-powered">Powered by Opendate</div>
    </div>
  );
};

FormPreview.propTypes = {
  fields: PropTypes.object.isRequired,
  channels: PropTypes.shape({ email: PropTypes.bool, sms: PropTypes.bool }).isRequired,
  letChoose: PropTypes.bool,
  consents: PropTypes.array.isRequired,
  questions: PropTypes.array,
  appearance: PropTypes.object.isRequired,
  text: PropTypes.object.isRequired,
  success: PropTypes.shape({ mode: PropTypes.string, message: PropTypes.string }).isRequired,
};

export default FormPreview;
